import React, { useState, useEffect } from "react";
import { useUserContext } from "../../Contexts/UserContext";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Button from "../form-components/input/Button";

const EditProfile: React.FC = () => {
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [middleName, setMiddleName] = useState<string>('');
    const [mobile, setMobile] = useState<string>('');
    const [address, setAddress] = useState<string>('');
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'fail'>('idle');

    const { userData } = useUserContext();
    const [profilePic, setProfilePic] = useState<string>('https://via.placeholder.com/150');
    const [profilePicFile, setProfilePicFile] = useState<File | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (userData?.profileImageUrl) {
            setProfilePic(userData.profileImageUrl);
        }
        setFirstName(userData?.firstName || '');
        setLastName(userData?.lastName || '');
        setMiddleName(userData?.middleName || '');
        setMobile(userData?.mobile || '');
        setAddress(userData?.address || '');
    }, [userData]);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfilePic(reader.result as string);
            };
            reader.readAsDataURL(file);
            setProfilePicFile(file);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('loading');
        const token = sessionStorage.getItem('token');

        const formData = new FormData();
        formData.append('firstName', firstName);
        formData.append('lastName', lastName);
        formData.append('middleName', middleName);
        formData.append('mobile', mobile);
        formData.append('address', address);

        if (profilePicFile) {
            formData.append('profilePic', profilePicFile);
        }

        try {
            const response = await fetch('https://backend-kyw8.onrender.com/edit-profile', {
                method: 'PUT',
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
                body: formData,
            });
            const responseText = await response.text();
            console.log('Raw response:', responseText);

            if (response.ok) {
                toast.success('Profile updated successfully!', {
                    position: 'top-center',
                });
                setStatus('success');
                navigate('/profile');
            } else {
                toast.error('Profile update failed.', {
                    position: 'bottom-right',
                });
                setStatus('fail');
            }
        } catch (error) {
            toast.error('An error occurred while updating the profile.', {
                position: 'bottom-right',
            });
            setStatus('fail');
            console.error("An error occurred while updating the profile:", error);
        }
    };

    return (
        <div className="bg-gradient-to-r from-white to-lightbrown h-45 w-full flex items-center justify-center p-40">
            <div className="lg:border rounded-lg mt-20 lg:shadow-lg lg:bg-[#D9D9D9] lg:bg-opacity-80 p-8">
                <p className="text-xl font-semibold text-center mb-4">Edit Profile</p>
                <div className="flex flex-col lg:flex-row gap-10">
                    <div className="flex flex-col items-center space-y-4">
                        <div className="relative group">
                            <img
                                src={profilePic}
                                alt="Profile"
                                className="w-50 h-60 object-cover border-4 border-gray-200"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                <label htmlFor="file-input" className="cursor-pointer text-white text-sm font-semibold">
                                    Change Picture
                                </label>
                                <input
                                    id="file-input"
                                    type="file"
                                    className="hidden"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                />
                            </div>
                        </div>
                        <button
                            className="button-primary"
                            onClick={() => document.getElementById("file-input")?.click()}
                        >
                            Upload New Picture
                        </button>
                    </div>
                    <div>
                        <form onSubmit={handleSubmit}>
                            <div className='flex flex-col lg:flex-row lg:gap-10'>
                                <div className='mb-2'>
                                    <label htmlFor='firstname' className='input-label'>
                                        First Name
                                    </label>
                                    <input
                                        type='text'
                                        id='firstname'
                                        value={firstName}
                                        placeholder='First Name'
                                        onChange={(e) => setFirstName(e.target.value)}
                                        className='input'
                                    />
                                </div>
                                <div className='mb-2'>
                                    <label htmlFor='lastname' className='input-label'>
                                        Last Name
                                    </label>
                                    <input
                                        type='text'
                                        id='lastname'
                                        value={lastName}
                                        placeholder='Last Name'
                                        onChange={(e) => setLastName(e.target.value)}
                                        className='input'
                                    />
                                </div>
                            </div>
                            <div className='flex flex-col lg:flex-row lg:gap-5'>
                                <div className='mb-2'>
                                    <label htmlFor='middlename' className='input-label'>
                                        Middle Name
                                    </label>
                                    <input
                                        type='text'
                                        id='middlename'
                                        value={middleName}
                                        placeholder='Middle Name'
                                        onChange={(e) => setMiddleName(e.target.value)}
                                        className='input'
                                    />
                                </div>
                                <div className='mb-2'>
                                    <label htmlFor="mobile" className='input-label'>
                                        Phone Number
                                    </label>
                                    <input
                                        type='text'
                                        id='mobile'
                                        value={mobile}
                                        placeholder='Phone Number'
                                        onChange={(e) => setMobile(e.target.value)}
                                        className='input'
                                    />
                                </div>
                            </div>
                            <div className='mb-4'>
                                <label htmlFor="address" className='input-label'>
                                    Address
                                </label>
                                <input
                                    type='text'
                                    id='address'
                                    value={address}
                                    placeholder='Address'
                                    onChange={(e) => setAddress(e.target.value)}
                                    className='input'
                                />
                            </div>
                            <Button
                                type='submit'
                                isLoading={status === 'loading'}
                                buttonText='Save Changes'
                            />
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EditProfile;
